'use client';

import React, { useState } from 'react';
import { Popover, PopoverTrigger, PopoverContent, Button, Input } from '@heroui/react';
import { Checkbox } from './Checkbox';
import Typography from '../typography/Typography';

interface OfferFilterSelectProps {
  placeholder: string;
  commission: string;
  guaranteed: boolean;
  // arpu?: string;
  onCommissionChange: (value: string) => void;
  onGuaranteedChange: (value: boolean) => void;
  // onArpuChange?: (value: string) => void;
  startContent?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  variant?: 'default' | 'campaign';
  autoWidth?: boolean;
  t?: (key: string) => string;
}

const OfferFilterSelect: React.FC<OfferFilterSelectProps> = ({
  placeholder,
  commission,
  guaranteed,
  // arpu = "",
  onCommissionChange,
  onGuaranteedChange,
  // onArpuChange,
  startContent,
  className = '',
  disabled = false,
  variant = 'default',
  autoWidth = false,
  t = (key: string) => key,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const getVariantStyles = () => {
    switch (variant) {
      case 'campaign':
        return {
          trigger: 'bg-auth-surface border-none rounded-2 !px-3 !min-h-9 !h-9',
          value: 'text-sm !text-auth-muted',
        };
      default:
        return {
          trigger:
            'h-9 bg-gray-100 border-none rounded-2 !px-3 hover:bg-gray-200 focus:border-blue-500',
          value: 'text-sm !text-auth-muted',
        };
    }
  };

  const variantStyles = getVariantStyles();

  // Check if any filter is active
  const hasActiveFilters = commission !== '' || guaranteed;

  // Get display text
  const getDisplayText = () => {
    if (!hasActiveFilters) return placeholder;

    const parts = [];
    if (commission) parts.push(`${commission}%`);
    if (guaranteed) {
      parts.push(t('partner.revshare.offers.filters.guaranteedPayment') || 'С гарантией');
    }
    return parts.length > 0 ? parts.join(', ') : placeholder;
  };

  return (
    <div className={autoWidth ? 'w-fit max-w-[20rem]' : 'w-full'}>
      <Popover
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        placement="bottom-start"
        classNames={{
          content: 'max-w-[21.25rem] w-[21.25rem] py-4 px-4',
        }}
      >
        <PopoverTrigger>
          <Button
            className={`${variantStyles.trigger} ${
              disabled ? 'opacity-50 cursor-not-allowed' : ''
            } ${autoWidth ? 'w-fit max-w-[20rem]' : 'w-full'} ${className} justify-start`}
            disabled={disabled}
          >
            <div className="flex items-center gap-1 w-full">
              {startContent}
              <span
                className={`${hasActiveFilters ? 'text-sm !text-auth-ink' : variantStyles.value} truncate flex-1 text-left`}
              >
                {getDisplayText()}
              </span>
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent>
          <div className="w-full flex flex-col gap-3">
            {/* Commission filter */}
            <div className="flex flex-col gap-2">
              <Typography variant="body2">
                {t('partner.revshare.offers.filters.commission') || 'Комиссия'}
              </Typography>
              <Input
                type="number"
                placeholder="<"
                size="sm"
                value={commission}
                onChange={(e) => onCommissionChange(e.target.value)}
                classNames={{
                  input: 'text-sm',
                  inputWrapper:
                    '!min-h-9 !max-h-9 bg-transparent border border-auth-strength-none rounded-2 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]',
                }}
              />
            </div>

            {/* Guaranteed filter */}
            <div className="flex items-center">
              <Checkbox
                isSelected={guaranteed}
                onChange={onGuaranteedChange}
                label={
                  t('partner.revshare.offers.filters.guaranteedPaymentCheckbox') ||
                  'Офферы с гарантированным платежом'
                }
              />
            </div>

            {/* ARPU filter - commented out */}
            {/* <div className="flex flex-col gap-2">
              <Typography variant="body2">
                {t('partner.revshare.offers.filters.arpu') || 'ARPU'}
              </Typography>
              <Input
                type="number"
                placeholder={t('partner.revshare.offers.filters.arpuPlaceholder') || 'От 0'}
                size="sm"
                value={arpu}
                onChange={(e) => onArpuChange?.(e.target.value)}
                endContent={<span className="text-auth-muted text-sm">$</span>}
                classNames={{
                  input: "text-sm",
                  inputWrapper: "!min-h-9 !max-h-9 bg-auth-surface",
                }}
              />
            </div> */}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default OfferFilterSelect;
